import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Loader2, Sparkles } from "lucide-react";

import { enqueueSnackbar } from "notistack";
import { TOAST } from "@/shared/constants/toastMessages";

import { Button } from "@/shared/components/ui/button";
import { AccountRow } from "@/pages/postloss/modules/components/AccountRow";
import { buildItemSubtitle } from "@/pages/postloss/modules/components/itemSubtitles";
import { AcceptDeclineActions } from "@/shared/components/EstateItems/AcceptDeclineActions";
import { CategoryEditDialog } from "@/shared/components/EstateItems/CategoryEditDialog";
import { SUPPORTED_CATEGORIES } from "@/shared/components/EstateItems/categoryEditHelpers";
import { EditEstateItemModal } from "@/shared/components/EstateItems/EditEstateItemModal";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItemActions } from "@/shared/hooks/useEstateItemActions";
import pluralize from "@/shared/utils/pluralize";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import type { EstateItem } from "@/shared/types/estateTypes";

interface ReviewParsedItemsConfig {
  categories: string[];
  sectionLabel: string;
}

function groupByCategory(items: EstateItem[]): Record<string, EstateItem[]> {
  const groups: Record<string, EstateItem[]> = {};
  for (const item of items) {
    if (!groups[item.category]) {
      groups[item.category] = [];
    }
    groups[item.category].push(item);
  }
  return groups;
}

export function createReviewParsedItems(
  config: ReviewParsedItemsConfig,
): FC<FlowCustomComponentProps> {
  const ReviewParsedItems: FC<FlowCustomComponentProps> = ({ onComplete }) => {
    const { person } = useAppContext();
    const estateId = person?.estate_id;
    const firstName = person?.first_name ?? "their";
    const {
      assets,
      debts,
      isLoading,
      approveItem,
      updateItem,
      isUpdatingItem,
      deleteItem,
      isDeletingItem,
      itemActions: { handleAccept, handleDecline, getItemLoadingAction },
    } = useEstateItemActions(estateId);
    const [isProcessing, setIsProcessing] = useState(false);
    const [editItem, setEditItem] = useState<EstateItem | null>(null);
    const hasAdvanced = useRef(false);
    const [hiddenIds, setHiddenIds] = useState<Set<number>>(() => new Set());

    const allItems = useMemo(() => [...assets, ...debts], [assets, debts]);

    const visibleItems = useMemo(
      () =>
        allItems.filter(
          (item) =>
            config.categories.includes(item.category) &&
            item.extraction_status !== "declined" &&
            !hiddenIds.has(item.id),
        ),
      [allItems, hiddenIds],
    );

    const pendingItems = useMemo(
      () => visibleItems.filter((item) => item.extraction_status === "pending"),
      [visibleItems],
    );

    const allProcessed = pendingItems.length === 0;
    const grouped = useMemo(() => groupByCategory(visibleItems), [visibleItems]);

    const sourceDocNames = useMemo(() => {
      const docs = new Map<number, string>();
      for (const item of visibleItems) {
        if (item.source_document) {
          docs.set(
            item.source_document.id,
            item.source_document.display_name ?? item.source_document.file_name,
          );
        }
      }
      return [...docs.values()];
    }, [visibleItems]);

    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;

    useEffect(() => {
      if (
        estateId &&
        !isLoading &&
        visibleItems.length === 0 &&
        hiddenIds.size === 0 &&
        !hasAdvanced.current
      ) {
        hasAdvanced.current = true;
        onCompleteRef.current();
      }
    }, [estateId, isLoading, visibleItems.length, hiddenIds.size]);

    if (!estateId || isLoading || visibleItems.length === 0) {
      return null;
    }

    const handleAcceptAll = async () => {
      if (pendingItems.length === 0) return;
      setIsProcessing(true);
      try {
        const results = await Promise.allSettled(pendingItems.map((item) => approveItem(item.id)));
        const failedCount = results.filter((r) => r.status === "rejected").length;
        const successCount = results.length - failedCount;
        if (failedCount > 0) {
          enqueueSnackbar(`${failedCount} item(s) couldn't be accepted. Please try again.`, { variant: "error" });
        }
        if (successCount > 0) {
          enqueueSnackbar(`${successCount} item(s) added to inventory`, { variant: "success" });
        }
      } catch {
        enqueueSnackbar(TOAST.GENERIC_ERROR, { variant: "error" });
      } finally {
        setIsProcessing(false);
      }
    };

    const onDecline = async (id: number) => {
      setHiddenIds((prev) => new Set(prev).add(id));
      const success = await handleDecline(id);
      setHiddenIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      if (!success) return;
    };

    const renderItemActions = (item: EstateItem) => {
      if (item.extraction_status !== "pending") return undefined;
      return (
        <AcceptDeclineActions
          onAccept={() => handleAccept(item.id)}
          onDecline={() => onDecline(item.id)}
          loadingAction={getItemLoadingAction(item.id)}
          disabled={isProcessing}
        />
      );
    };

    const subtitle = allProcessed
      ? `Review these details from ${firstName}'s documents and edit anything that needs changing before we add them to the Estate Inventory.`
      : `Take a minute to review these. Edit anything that looks off, and we'll add them to ${firstName}'s Estate Inventory.`;

    return (
      <div className="flex flex-col gap-6">
        <div className="text-center">
          <h2>
            We found {visibleItems.length} of {firstName}&apos;s {config.sectionLabel}
          </h2>
          <p className="mt-2 text-secondary">{subtitle}</p>
        </div>

        {!allProcessed && sourceDocNames.length > 0 && (
          <div className="flex items-start gap-3 rounded-lg border border-brand bg-green-50 p-4">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-subtle">
              <Sparkles className="h-3.5 w-3.5 text-brand" />
            </div>
            <div>
              <p className="font-medium">
                {pendingItems.length} new {pluralize("item", pendingItems.length)} from{" "}
                {sourceDocNames.join(", ")}
              </p>
              <p className="text-sm text-secondary">Review and accept the following items</p>
            </div>
          </div>
        )}

        <div className="mx-auto flex w-full max-w-2xl flex-col gap-6 rounded-lg border border-primary bg-white p-6">
          {Object.entries(grouped).map(([category, items]) => (
            <div key={category} className="flex flex-col gap-2">
              <p className="text-base font-semibold">{category}</p>
              {items.map((item) => (
                <AccountRow
                  key={item.id}
                  item={item}
                  subtitle={buildItemSubtitle(item)}
                  onEdit={setEditItem}
                  actions={renderItemActions(item)}
                />
              ))}
            </div>
          ))}
        </div>

        <Button
          variant="brand"
          rounded
          className="mx-auto w-full max-w-[400px]"
          onClick={allProcessed ? () => onComplete() : handleAcceptAll}
          disabled={!allProcessed && isProcessing}
        >
          {!allProcessed && isProcessing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              {allProcessed ? "Continue" : "Accept all"}
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>

        {editItem && SUPPORTED_CATEGORIES.has(editItem.category) ? (
          <CategoryEditDialog
            key={editItem.id}
            item={editItem}
            isOpen
            onClose={() => setEditItem(null)}
            updateItem={updateItem}
            isUpdatingItem={isUpdatingItem}
            deleteItem={deleteItem}
            isDeletingItem={isDeletingItem}
          />
        ) : editItem ? (
          <EditEstateItemModal
            key={editItem.id}
            isOpen
            onClose={() => setEditItem(null)}
            estateId={editItem.estate_id}
            item={editItem}
          />
        ) : null}
      </div>
    );
  };

  return ReviewParsedItems;
}
